import { useMe } from "@/api/services/main/auth";
import { mintOauthRedirectApi } from "@/api/services/main/user";
import { PRODUCT_CODE_QUERY_KEY, REDIRECT_URL_QUERY_KEY } from "@/features/auth/constant/login";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { getToken } from "@/utils/token";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { FC, useEffect, useRef } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

export const MintOauthRedirectPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isProcessing = useRef(false);
  const { data: me, isLoading: isLoadingMe } = useMe();

  const productCode = searchParams.get(PRODUCT_CODE_QUERY_KEY);
  const redirectUrl = searchParams.get(REDIRECT_URL_QUERY_KEY);

  useEffect(() => {
    if (!productCode) return;
    if (isLoadingMe) return;

    // No matching per-user product-contract grant -> back to home (not an error).
    const hasContract = (me?.data.productContracts ?? []).some((pc) => pc.productCode === productCode);
    if (!hasContract) {
      navigate(AppRoutes.home, { replace: true });
      return;
    }

    const { refreshToken } = getToken();
    if (!refreshToken) {
      navigate(AppRoutes.login, { replace: true });
      return;
    }

    // Prevent double execution in StrictMode
    if (isProcessing.current) return;
    isProcessing.current = true;

    const mint = async () => {
      try {
        const { data } = await mintOauthRedirectApi({ productCode, refreshToken });
        const target = `${data.redirectURL}&redirectUrl=${encodeURIComponent(redirectUrl ?? "/")}`;

        globalThis.location.replace(target);
      } catch (error) {
        showError(error);
        navigate(AppRoutes.home, { replace: true });
      }
    };

    mint();
  }, [productCode, navigate, isLoadingMe, me, redirectUrl]);

  // No productCode -> back to home
  if (!productCode) {
    return <Navigate to={AppRoutes.home} replace />;
  }

  return (
    <Stack alignItems="center" justifyContent="center" height="100vh" gap={2}>
      <CircularProgress />
      <Typography variant="body14" color={text.secondary}>
        プロダクトへ移動中...
      </Typography>
    </Stack>
  );
};
